import { Pause, Play, RotateCcw } from "lucide-react";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

import type { Racecard } from "@/api";
import { reportClientIncident } from "@/app/nativeRecovery";

import { TwoDRace } from "./TwoDRace";
import {
  formatRaceTime,
  liveElapsedSeconds,
  replayElapsedSeconds,
} from "./twoDBroadcast";

export type RacePlayback = {
  raceId: string;
  status: string;
  startedAt: string;
  gatesOpenAt: string;
  serverTime: string;
  raceDurationMs: number;
  order: number[];
  finishTimesMs: number[];
  marginsL100?: number[];
  physics: Record<string, unknown>;
};

export type CameraMode = "auto" | "cinematic" | "rail" | "crane" | "ride";
export type RendererLifecycle =
  "idle" | "probing" | "loading" | "ready" | "lost" | "restoring" | "failed";
export type FinishSequencePhase = "replay" | "complete";

const RESULT_CAMERA_SETTLE_MS = 220;

export function RaceRenderer({
  card,
  playback,
  mode,
  cameraMode,
  rideGate,
  clockOffsetMs,
  replay = false,
  finishSequence = false,
  fullscreen = false,
  onFinishSequence,
  onUnavailable,
  onVisualReady,
  onProgress,
}: {
  card: Racecard;
  playback: RacePlayback | null;
  mode: "3d" | "2d";
  cameraMode: CameraMode;
  rideGate: number;
  clockOffsetMs: number;
  replay?: boolean;
  /** Play the casino ending: slow-mo podium replay before results. */
  finishSequence?: boolean;
  fullscreen?: boolean;
  onFinishSequence?: (phase: FinishSequencePhase) => void;
  onUnavailable?: (message: string) => void;
  onVisualReady?: (ready: boolean) => void;
  /** Coarse percentage; 100 is reserved for a presentation-ready frame. */
  onProgress?: (progress: number) => void;
}) {
  const callbackRef = useRef({ onUnavailable, onVisualReady, onProgress });
  const presentationKey = `${mode}:${card.raceId}:${playback?.raceId === card.raceId ? playback.startedAt : "gates"}`;
  const presentationKeyRef = useRef(presentationKey);
  useLayoutEffect(() => {
    callbackRef.current = { onUnavailable, onVisualReady, onProgress };
    presentationKeyRef.current = presentationKey;
  }, [onUnavailable, onVisualReady, onProgress, presentationKey]);
  const [readyKey, setReadyKey] = useState<string | null>(null);
  const progressRef = useRef(0);
  const handleVisualReady = useCallback((ready: boolean) => {
    setReadyKey(ready ? presentationKeyRef.current : null);
    if (ready) {
      progressRef.current = 100;
      callbackRef.current.onProgress?.(100);
    } else if (progressRef.current === 100) {
      progressRef.current = 0;
      callbackRef.current.onProgress?.(0);
    }
    callbackRef.current.onVisualReady?.(ready);
  }, []);
  const handleProgress = useCallback((progress: number) => {
    const next = Math.max(
      progressRef.current,
      Math.max(0, Math.min(95, progress)),
    );
    progressRef.current = next;
    callbackRef.current.onProgress?.(next);
  }, []);

  useLayoutEffect(() => {
    progressRef.current = 0;
    callbackRef.current.onProgress?.(0);
    callbackRef.current.onVisualReady?.(false);
  }, [presentationKey]);

  const [lifecycle, setLifecycle] = useState<RendererLifecycle>(
    mode === "3d" ? "probing" : "idle",
  );
  const [fallbackMessage, setFallbackMessage] = useState("");
  const playbackClock = usePlaybackClock({
    playback,
    clockOffsetMs,
    replay,
  });
  // The 2D view warps the SAME replay clock the 3D scene plays natively,
  // so both renderers deliver the casino's slow-motion podium ending.
  const twoDWarp = useTwoDFinishWarp({
    enabled: finishSequence && !replay && mode === "2d",
    playback,
    getElapsedSeconds: playbackClock.getElapsedSeconds,
    onFinishSequence,
  });

  const handleLifecycle = useCallback((state: RendererLifecycle) => {
    setLifecycle(state);
    if (state === "ready") setFallbackMessage("");
  }, []);

  const handleFailure = useCallback(
    (message: string) => {
      setLifecycle("failed");
      setFallbackMessage(message);
      handleVisualReady(false);
      callbackRef.current.onUnavailable?.(message);
    },
    [handleVisualReady],
  );

  const [replayReadyRaceId, setReplayReadyRaceId] = useState<string | null>(
    null,
  );
  const replayReadyRaceRef = useRef<string | null>(null);
  const replayRaceId = playback?.raceId ?? null;
  const startReplay = playbackClock.start;
  // A historical replay begins only once its renderer can present it.
  // Neither a cold 3D load nor a deferred 2D paint may consume hidden time.
  const handlePlaybackStarted = useCallback(
    (raceId: string) => {
      if (!replay || replayRaceId !== raceId) return;
      if (replayReadyRaceRef.current === raceId) return;
      replayReadyRaceRef.current = raceId;
      setReplayReadyRaceId(raceId);
      startReplay();
    },
    [replay, replayRaceId, startReplay],
  );
  const replayControlsReady = replayReadyRaceId === replayRaceId;
  const handleTwoDReady = useCallback(() => {
    if (mode !== "2d" || presentationKeyRef.current !== presentationKey) return;
    if (playback?.raceId === card.raceId)
      handlePlaybackStarted(playback.raceId);
    handleVisualReady(true);
  }, [
    card.raceId,
    handlePlaybackStarted,
    handleVisualReady,
    mode,
    playback,
    presentationKey,
  ]);
  const handleTwoDError = useCallback(() => {
    if (mode !== "2d" || presentationKeyRef.current !== presentationKey) return;
    // Reveal TwoDRace's honest error UI, but never start an unpainted replay.
    handleVisualReady(true);
  }, [handleVisualReady, mode, presentationKey]);

  useEffect(() => {
    if (!replay) replayReadyRaceRef.current = null;
  }, [replay]);

  // Preserve healthy scenes across mode/route layout toggles. A failed scene
  // must leave the tree on fallback so an explicit 3D retry boots a fresh one.
  const render3d =
    mode === "3d" || (lifecycle !== "idle" && lifecycle !== "failed");
  return (
    <div
      className="race-renderer-stack"
      data-mode={mode}
      data-presentation-ready={readyKey === presentationKey}
      data-renderer-state={mode === "3d" ? lifecycle : "2d"}
      data-visual-elapsed-ms={Math.round(playbackClock.elapsedMs)}
    >
      {render3d ? (
        <ThreeDRace
          active={mode === "3d"}
          cameraMode={cameraMode}
          card={card}
          finishSequence={finishSequence && !replay}
          getElapsedSeconds={playbackClock.getElapsedSeconds}
          onFailure={handleFailure}
          onFinishSequence={onFinishSequence}
          onLifecycle={handleLifecycle}
          onPlaybackStarted={handlePlaybackStarted}
          onProgress={handleProgress}
          onVisualReady={handleVisualReady}
          playback={playback}
          playbackRevision={playbackClock.revision}
          rideGate={rideGate}
        />
      ) : null}
      <TwoDRace
        active={mode === "2d"}
        card={card}
        fullscreen={fullscreen}
        getElapsedSeconds={twoDWarp.getElapsedSeconds}
        onReady={handleTwoDReady}
        onError={handleTwoDError}
        playback={playback}
        replay={replay}
      />
      {replay && playback && replayControlsReady ? (
        <div
          className="race-replay-controls"
          data-playing={playbackClock.playing}
        >
          <button
            aria-label={playbackClock.playing ? "Pause replay" : "Play replay"}
            onClick={playbackClock.toggle}
            type="button"
          >
            {playbackClock.playing ? (
              <Pause aria-hidden size={15} />
            ) : (
              <Play aria-hidden size={15} />
            )}
          </button>
          <button
            aria-label="Restart replay"
            onClick={playbackClock.restart}
            type="button"
          >
            <RotateCcw aria-hidden size={14} />
          </button>
          <div
            aria-label="Replay progress"
            aria-valuemax={playbackClock.durationMs}
            aria-valuemin={0}
            aria-valuenow={playbackClock.elapsedMs}
            className="race-replay-controls__progress"
            role="progressbar"
          >
            <span
              style={{
                width: `${Math.min(
                  100,
                  (playbackClock.elapsedMs /
                    Math.max(1, playbackClock.durationMs)) *
                    100,
                )}%`,
              }}
            />
          </div>
          <time>
            {formatRaceTime(playbackClock.elapsedMs / 1_000)} /{" "}
            {formatRaceTime(playbackClock.durationMs / 1_000)}
          </time>
        </div>
      ) : null}
      {fallbackMessage && mode === "2d" ? (
        <div className="race-renderer__fallback" role="status">
          3D paused: {fallbackMessage}.
        </div>
      ) : null}
    </div>
  );
}

// Live-clock corrections are slewed, never stepped: every playback/market
// poll re-measures the server offset through fresh network jitter, and
// applying each measurement directly snaps race time by that jitter — every
// horse (and the camera chasing them) visibly jumps mid-gallop every few
// seconds on a real WAN. Bound the correction rate instead; a >250ms error
// is a genuine seek (tab slept, clock stepped) and still snaps.
const OFFSET_SLEW_PER_SECOND_MS = 30;
const OFFSET_SNAP_THRESHOLD_MS = 250;

function usePlaybackClock({
  playback,
  clockOffsetMs,
  replay,
}: {
  playback: RacePlayback | null;
  clockOffsetMs: number;
  replay: boolean;
}) {
  const playbackRef = useRef(playback);
  const offsetRef = useRef(clockOffsetMs);
  const offsetSlewRef = useRef<{
    raceId: string;
    appliedMs: number;
    atMs: number;
  } | null>(null);
  const replayRef = useRef(replay);
  const clockRef = useRef({
    raceId: playback?.raceId ?? null,
    replay,
    playing: false,
    accumulatedMs: 0,
    startedAtMs: 0,
    revision: 0,
  });
  const [display, setDisplay] = useState({
    playing: false,
    elapsedMs: 0,
    durationMs: playback?.raceDurationMs ?? 0,
    revision: 0,
  });

  useLayoutEffect(() => {
    playbackRef.current = playback;
  }, [playback]);

  useLayoutEffect(() => {
    offsetRef.current = clockOffsetMs;
  }, [clockOffsetMs]);

  useLayoutEffect(() => {
    replayRef.current = replay;
  }, [replay]);

  useLayoutEffect(() => {
    const activePlayback = playbackRef.current;
    const revision = clockRef.current.revision + 1;
    const playing = false;
    clockRef.current = {
      raceId: activePlayback?.raceId ?? null,
      replay,
      playing,
      accumulatedMs: 0,
      startedAtMs: performance.now(),
      revision,
    };
    setDisplay({
      playing,
      elapsedMs: replay
        ? 0
        : activePlayback
          ? liveElapsedSeconds({
              nowMs: Date.now(),
              serverOffsetMs: offsetRef.current,
              gatesOpenAt: activePlayback.gatesOpenAt,
            }) * 1_000
          : 0,
      durationMs: activePlayback?.raceDurationMs ?? 0,
      revision,
    });
  }, [playback?.raceId, replay]);

  const getElapsedSeconds = useCallback(() => {
    const activePlayback = playbackRef.current;
    if (!activePlayback) return 0;
    const clock = clockRef.current;
    if (!replayRef.current) {
      const targetMs = offsetRef.current;
      const nowPerfMs = performance.now();
      const previous = offsetSlewRef.current;
      // A race change is a legitimate discontinuity: the next race starts
      // on the freshest measured offset instead of slewing across rounds.
      const slew =
        previous && previous.raceId === activePlayback.raceId
          ? previous
          : {
              raceId: activePlayback.raceId,
              appliedMs: targetMs,
              atMs: nowPerfMs,
            };
      const errorMs = targetMs - slew.appliedMs;
      let appliedMs;
      if (Math.abs(errorMs) > OFFSET_SNAP_THRESHOLD_MS) {
        appliedMs = targetMs;
      } else {
        const budgetMs =
          (Math.max(0, nowPerfMs - slew.atMs) / 1000) *
          OFFSET_SLEW_PER_SECOND_MS;
        appliedMs =
          slew.appliedMs + Math.max(-budgetMs, Math.min(budgetMs, errorMs));
      }
      offsetSlewRef.current = {
        raceId: slew.raceId,
        appliedMs,
        atMs: nowPerfMs,
      };
      return liveElapsedSeconds({
        nowMs: Date.now(),
        serverOffsetMs: appliedMs,
        gatesOpenAt: activePlayback.gatesOpenAt,
      });
    }
    if (clock.startedAtMs === 0) return 0;
    return replayElapsedSeconds({
      accumulatedMs: clock.accumulatedMs,
      startedAtMs: clock.startedAtMs,
      nowMs: performance.now(),
      playing: clock.playing,
      raceDurationMs: activePlayback.raceDurationMs,
    });
  }, []);

  useEffect(() => {
    if (!playback) return;
    const update = () => {
      const elapsedMs = getElapsedSeconds() * 1_000;
      const clock = clockRef.current;
      if (
        replayRef.current &&
        clock.playing &&
        elapsedMs >= playback.raceDurationMs
      ) {
        clock.accumulatedMs = playback.raceDurationMs;
        clock.playing = false;
      }
      setDisplay((current) => {
        const roundedElapsed = Math.round(elapsedMs / 100) * 100;
        if (
          current.elapsedMs === roundedElapsed &&
          current.playing === clock.playing &&
          current.durationMs === playback.raceDurationMs &&
          current.revision === clock.revision
        ) {
          return current;
        }
        return {
          playing: clock.playing,
          elapsedMs: roundedElapsed,
          durationMs: playback.raceDurationMs,
          revision: clock.revision,
        };
      });
    };
    update();
    const interval = window.setInterval(update, 100);
    return () => window.clearInterval(interval);
  }, [getElapsedSeconds, playback]);

  const toggle = useCallback(() => {
    const activePlayback = playbackRef.current;
    if (!activePlayback || !replayRef.current) return;
    const clock = clockRef.current;
    const now = performance.now();
    if (clock.playing) {
      clock.accumulatedMs = getElapsedSeconds() * 1_000;
      clock.playing = false;
    } else {
      if (clock.accumulatedMs >= activePlayback.raceDurationMs) {
        clock.accumulatedMs = 0;
        clock.revision += 1;
      }
      clock.startedAtMs = now;
      clock.playing = true;
    }
    setDisplay((current) => ({
      ...current,
      playing: clock.playing,
      elapsedMs: Math.round(clock.accumulatedMs / 100) * 100,
      revision: clock.revision,
    }));
  }, [getElapsedSeconds]);

  // Asset/environment/GPU preparation now gates replay start. Retain the
  // existing opening-frame stall forgiveness for residual device stalls:
  // through a replay's opening the clock counts only time the viewer could
  // actually watch. It starts at the first painted
  // frame, and each later frame gap >600ms is forgiven except for a
  // 100ms slice — a true stall reads as barely-advancing, never a rewind
  // and never frozen (sustained gap streams still show visible progress
  // to any observer), while a struggling-but-alive few-fps cadence
  // advances normally. Forgiveness ends at the first frame past the
  // window; the clock is plain wall time from then on.
  const startStallForgivenessRef = useRef<object | null>(null);
  const startFrameRef = useRef(0);
  useEffect(
    () => () => {
      startStallForgivenessRef.current = null;
      window.cancelAnimationFrame(startFrameRef.current);
    },
    [],
  );
  const rezeroAfterPaint = useCallback(
    (clock: {
      playing: boolean;
      accumulatedMs: number;
      startedAtMs: number;
    }) => {
      const token = {};
      window.cancelAnimationFrame(startFrameRef.current);
      startStallForgivenessRef.current = token;
      const deadlineMs = performance.now() + 8_000;
      let lastFrameAtMs: number | null = null;
      const tick = () => {
        if (
          startStallForgivenessRef.current !== token ||
          clockRef.current !== clock ||
          !clock.playing ||
          clock.accumulatedMs !== 0
        ) {
          return;
        }
        const nowMs = performance.now();
        if (lastFrameAtMs === null) {
          // The first painted frame after start (however long the swap's
          // first compile stall ran): the replay begins here.
          clock.startedAtMs = nowMs;
        } else {
          const gapMs = nowMs - lastFrameAtMs;
          if (gapMs > 600) {
            clock.startedAtMs = Math.min(
              nowMs,
              clock.startedAtMs + gapMs - 100,
            );
          }
        }
        lastFrameAtMs = nowMs;
        if (nowMs > deadlineMs) return;
        startFrameRef.current = window.requestAnimationFrame(tick);
      };
      startFrameRef.current = window.requestAnimationFrame(tick);
    },
    [],
  );

  const start = useCallback(() => {
    const activePlayback = playbackRef.current;
    if (!activePlayback || !replayRef.current) return;
    const clock = clockRef.current;
    clock.accumulatedMs = 0;
    clock.startedAtMs = performance.now();
    clock.playing = true;
    rezeroAfterPaint(clock);
    setDisplay({
      playing: true,
      elapsedMs: 0,
      durationMs: activePlayback.raceDurationMs,
      revision: clock.revision,
    });
  }, [rezeroAfterPaint]);

  const restart = useCallback(() => {
    const activePlayback = playbackRef.current;
    if (!activePlayback || !replayRef.current) return;
    const clock = clockRef.current;
    clock.accumulatedMs = 0;
    clock.startedAtMs = performance.now();
    clock.playing = true;
    clock.revision += 1;
    rezeroAfterPaint(clock);
    setDisplay({
      playing: true,
      elapsedMs: 0,
      durationMs: activePlayback.raceDurationMs,
      revision: clock.revision,
    });
  }, [rezeroAfterPaint]);

  return {
    ...display,
    getElapsedSeconds,
    start,
    toggle,
    restart,
  };
}

/** Casino ending for the 2D view: once the podium is home, the paint
 * clock re-runs the finish through the donor's pure slow-motion replay
 * timeline, then reports completion so the result presentation begins. */
function useTwoDFinishWarp({
  enabled,
  playback,
  getElapsedSeconds,
  onFinishSequence,
}: {
  enabled: boolean;
  playback: RacePlayback | null;
  getElapsedSeconds: () => number;
  onFinishSequence?: (phase: FinishSequencePhase) => void;
}) {
  const stateRef = useRef<{
    key: string | null;
    phase: "idle" | "replay" | "done";
    replay: import("@renderer/zed/zed-replay.js").ZedFinishReplay | null;
    sampler:
      typeof import("@renderer/zed/zed-replay.js").sampleZedFinishReplay | null;
    target: Record<string, unknown>;
  }>({ key: null, phase: "idle", replay: null, sampler: null, target: {} });
  const notifyRef = useRef(onFinishSequence);
  const playbackRef = useRef(playback);
  useEffect(() => {
    notifyRef.current = onFinishSequence;
    playbackRef.current = playback;
  });

  // Keyed on the immutable round identity — never on refetch object
  // identity, which changes every poll and must not reset a replay
  // that is already on screen.
  const playbackKey =
    playback && enabled ? `${playback.raceId}:${playback.startedAt}` : null;

  useEffect(() => {
    stateRef.current = {
      key: playbackKey,
      phase: "idle",
      replay: null,
      sampler: null,
      target: {},
    };
    const activePlayback = playbackRef.current;
    if (!playbackKey || !activePlayback) return;
    let cancelled = false;
    let poll = 0;
    let doneTimer = 0;
    void Promise.all([
      import("@renderer/race-physics.js"),
      import("@renderer/zed/zed-replay.js"),
    ]).then(([{ planRace }, replayModule]) => {
      if (cancelled || stateRef.current.key !== playbackKey) return;
      const plan = planRace(activePlayback.physics);
      stateRef.current.sampler = replayModule.sampleZedFinishReplay;
      const complete = () => {
        const state = stateRef.current;
        if (state.key !== playbackKey || state.phase === "done") return;
        state.phase = "done";
        state.replay = null;
        notifyRef.current?.("complete");
      };
      // Nominal raceDurationMs is not a finish threshold. The visual clock
      // keeps advancing through actual finish times and gallop-out, so replay
      // starts only once the deterministic podium is physically home.
      const decidedS = plan.thirdFinish + 0.35;
      poll = window.setInterval(() => {
        const state = stateRef.current;
        if (state.key !== playbackKey || state.phase !== "idle") {
          window.clearInterval(poll);
          return;
        }
        if (getElapsedSeconds() < decidedS) return;
        window.clearInterval(poll);
        const replayTimeline = replayModule.createZedFinishReplay(plan, {
          startMs: performance.now(),
          reducedMotion: window.matchMedia("(prefers-reduced-motion: reduce)")
            .matches,
        });
        if (!replayTimeline.enabled) {
          complete();
          return;
        }
        state.replay = replayTimeline;
        state.phase = "replay";
        notifyRef.current?.("replay");
        doneTimer = window.setTimeout(complete, replayTimeline.durationMs + 60);
      }, 200);
    });
    return () => {
      cancelled = true;
      if (poll) window.clearInterval(poll);
      if (doneTimer) window.clearTimeout(doneTimer);
    };
  }, [getElapsedSeconds, playbackKey]);

  const warpedGetElapsedSeconds = useCallback(() => {
    const state = stateRef.current;
    if (state.phase === "replay" && state.replay && state.sampler) {
      const sample = state.sampler(
        state.replay,
        performance.now(),
        state.target,
      );
      return Number(sample.raceT) || 0;
    }
    return getElapsedSeconds();
  }, [getElapsedSeconds]);

  return { getElapsedSeconds: warpedGetElapsedSeconds };
}

function ThreeDRace({
  card,
  playback,
  cameraMode,
  rideGate,
  getElapsedSeconds,
  playbackRevision,
  active,
  finishSequence = false,
  onFinishSequence,
  onLifecycle,
  onPlaybackStarted,
  onFailure,
  onVisualReady,
  onProgress,
}: {
  card: Racecard;
  playback: RacePlayback | null;
  cameraMode: CameraMode;
  rideGate: number;
  getElapsedSeconds: () => number;
  playbackRevision: number;
  active: boolean;
  finishSequence?: boolean;
  onFinishSequence?: (phase: FinishSequencePhase) => void;
  onLifecycle: (state: RendererLifecycle) => void;
  onPlaybackStarted?: (raceId: string) => void;
  onFailure: (message: string) => void;
  onVisualReady: (ready: boolean) => void;
  onProgress: (progress: number) => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const sceneRef = useRef<import("@renderer/scene.js").RaceScene | null>(null);
  const cardRef = useRef(card);
  const playbackRef = useRef(playback);
  const cameraRef = useRef({ cameraMode, rideGate });
  const activeRef = useRef(active);
  const callbacksRef = useRef({
    onLifecycle,
    onFailure,
    onFinishSequence,
    onPlaybackStarted,
    onVisualReady,
    onProgress,
  });
  const sequenceRef = useRef<{
    key: string | null;
    state: "idle" | "replay" | "done";
    enabled: boolean;
    active: boolean;
  }>({ key: null, state: "idle", enabled: finishSequence, active });
  const configurationRef = useRef<{
    raceId: string;
    promise: Promise<void>;
  } | null>(null);
  const startedPlaybackRef = useRef<string | null>(null);
  const [sceneReady, setSceneReady] = useState(false);
  const [configuredRaceId, setConfiguredRaceId] = useState<string | null>(null);
  const [contextRevision, setContextRevision] = useState(0);
  const contextAvailableRef = useRef(true);
  const presentationConfiguredRef = useRef(false);
  const [error, setError] = useState("");
  const [lifecycle, setLifecycle] = useState<RendererLifecycle>("probing");
  const restoreTimerRef = useRef<number | null>(null);
  const generationRef = useRef(0);
  const mountedRef = useRef(false);
  const failRef = useRef<(message: string) => void>(() => {});
  const playbackKey = playback
    ? `${playback.raceId}:${playback.startedAt}:${playbackRevision}`
    : null;
  const playbackKeyRef = useRef(playbackKey);
  useLayoutEffect(() => {
    playbackKeyRef.current = playbackKey;
    callbacksRef.current = {
      onLifecycle,
      onFailure,
      onFinishSequence,
      onPlaybackStarted,
      onVisualReady,
      onProgress,
    };
    sequenceRef.current.enabled = finishSequence;
    sequenceRef.current.active = active;
    cardRef.current = card;
    playbackRef.current = playback;
    cameraRef.current = { cameraMode, rideGate };
    activeRef.current = active;
  }, [
    playbackKey,
    onLifecycle,
    onFailure,
    onFinishSequence,
    onPlaybackStarted,
    onVisualReady,
    onProgress,
    finishSequence,
    active,
    card,
    playback,
    cameraMode,
    rideGate,
  ]);
  const ensureSceneRef = useRef<() => void>(() => {});

  useEffect(() => {
    if (active) ensureSceneRef.current();
    sceneRef.current?.setActive(active);
    const frame = active
      ? window.requestAnimationFrame(() => sceneRef.current?.resize())
      : 0;
    return () => window.cancelAnimationFrame(frame);
  }, [active]);

  useEffect(() => {
    let cancelled = false;
    let failed = false;
    let mounting = false;
    let loadDeadline = 0;
    let scene: import("@renderer/scene.js").RaceScene | null = null;
    const canvas = canvasRef.current;
    if (!canvas) return;
    mountedRef.current = true;
    contextAvailableRef.current = true;
    setSceneReady(false);
    setConfiguredRaceId(null);
    configurationRef.current = null;
    presentationConfiguredRef.current = false;
    startedPlaybackRef.current = null;

    const updateLifecycle = (state: RendererLifecycle) => {
      if (cancelled || (failed && state !== "failed")) return;
      setLifecycle(state);
      callbacksRef.current.onLifecycle(state);
    };
    const fail = (message: string) => {
      if (cancelled || failed) return;
      failed = true;
      generationRef.current += 1;
      contextAvailableRef.current = false;
      if (restoreTimerRef.current) {
        window.clearTimeout(restoreTimerRef.current);
        restoreTimerRef.current = null;
      }
      setError(message);
      updateLifecycle("failed");
      if (activeRef.current) {
        callbacksRef.current.onVisualReady(false);
        callbacksRef.current.onFailure(message);
      }
    };
    failRef.current = fail;
    const onContextState = (event: Event) => {
      if (cancelled || failed) return;
      const detail = (
        event as CustomEvent<{ state?: RendererLifecycle; reason?: string }>
      ).detail;
      const state = detail?.state;
      if (!state) return;
      if (state === "lost" || state === "restoring") {
        if (state === "lost") reportClientIncident("webgl_context_lost");
        contextAvailableRef.current = false;
        generationRef.current += 1;
        setConfiguredRaceId(null);
        if (activeRef.current) callbacksRef.current.onVisualReady(false);
        setError("");
        updateLifecycle(state);
        if (!restoreTimerRef.current) {
          restoreTimerRef.current = window.setTimeout(() => {
            fail("WebGL context could not be restored");
          }, 3_000);
        }
        return;
      }
      if (state === "failed") {
        reportClientIncident("webgl_restore_failed");
        fail(detail.reason ?? "WebGL context restoration failed");
        return;
      }
      if (state === "ready") {
        if (restoreTimerRef.current) {
          window.clearTimeout(restoreTimerRef.current);
          restoreTimerRef.current = null;
        }
        contextAvailableRef.current = true;
        setError("");
        // A restored context is not proof that the latest race is warm.
        updateLifecycle("restoring");
        setContextRevision((revision) => revision + 1);
        return;
      }
      updateLifecycle(state);
    };
    canvas.addEventListener("race-renderer-context", onContextState);

    async function mount() {
      if (cancelled || failed || mounting || scene || !activeRef.current)
        return;
      mounting = true;
      loadDeadline = window.setTimeout(
        () => fail("Race assets timed out"),
        30_000,
      );
      updateLifecycle("probing");
      callbacksRef.current.onVisualReady(false);
      try {
        // React may clean up a StrictMode probe before this mount survives.
        // Do not even start its module fetch or allocate a renderer.
        await Promise.resolve();
        if (cancelled || !activeRef.current) return;
        const { RaceScene } = await import("@renderer/scene.js");
        if (cancelled || !activeRef.current || !contextAvailableRef.current)
          return;
        if (!canvas) return;
        scene = new RaceScene(canvas, { eagerPrewarm: false });
        sceneRef.current = scene;
        updateLifecycle("loading");
        scene.setReducedMotion(
          window.matchMedia("(prefers-reduced-motion: reduce)").matches,
        );
        const initialCard = cardRef.current;
        // Apply the field synchronously before load builds the runners, but
        // never wait on GPU readiness until the load promise has completed.
        const configured = configureScene(
          scene,
          initialCard,
          () => assetsReady,
          () =>
            !cancelled &&
            !failed &&
            cardRef.current.raceId === initialCard.raceId,
        );
        configurationRef.current = {
          raceId: initialCard.raceId,
          promise: configured,
        };
        void configured.catch(() => {}); // The configuration effect handles failure.
        const assetsReady = scene.load(
          (value) => {
            if (!cancelled && !failed && activeRef.current) {
              callbacksRef.current.onProgress(Math.min(95, value * 95));
            }
          },
          { applyDefaultEnvironment: false },
        );
        await assetsReady;
        if (cancelled || failed) return;
        scene.setActive(activeRef.current);
        setSceneReady(true);
      } catch (caught) {
        fail(caught instanceof Error ? caught.message : "Renderer failed");
      } finally {
        window.clearTimeout(loadDeadline);
        mounting = false;
      }
    }

    ensureSceneRef.current = () => {
      void mount();
    };
    void mount();
    return () => {
      cancelled = true;
      mountedRef.current = false;
      generationRef.current += 1;
      ensureSceneRef.current = () => {};
      window.clearTimeout(loadDeadline);
      canvas.removeEventListener("race-renderer-context", onContextState);
      if (restoreTimerRef.current) {
        window.clearTimeout(restoreTimerRef.current);
        restoreTimerRef.current = null;
      }
      sceneRef.current = null;
      scene?.dispose();
    };
  }, []);

  useEffect(() => {
    const scene = sceneRef.current;
    if (!active || !sceneReady || !scene || !contextAvailableRef.current)
      return;
    const generation = ++generationRef.current;
    let cancelled = false;
    presentationConfiguredRef.current = false;
    setConfiguredRaceId(null);
    callbacksRef.current.onVisualReady(false);
    setLifecycle("loading");
    callbacksRef.current.onLifecycle("loading");
    let configuration = configurationRef.current;
    if (configuration?.raceId !== card.raceId) {
      startedPlaybackRef.current = null;
      scene.clearRace({ rebuildField: false });
      configuration = {
        raceId: card.raceId,
        promise: configureScene(
          scene,
          cardRef.current,
          undefined,
          () =>
            mountedRef.current &&
            sceneRef.current === scene &&
            cardRef.current.raceId === card.raceId,
        ),
      };
      configurationRef.current = configuration;
    }
    const deadline = window.setTimeout(() => {
      if (!cancelled && generationRef.current === generation) {
        failRef.current("Race presentation timed out");
      }
    }, 30_000);
    void configuration.promise
      .then(() => {
        if (
          contextRevision > 0 &&
          !cancelled &&
          generationRef.current === generation
        ) {
          return scene.whenPresentationReady();
        }
      })
      .then(() => {
        if (
          cancelled ||
          !mountedRef.current ||
          !activeRef.current ||
          generationRef.current !== generation ||
          cardRef.current.raceId !== card.raceId
        )
          return;
        scene.setCameraMode(
          cameraRef.current.cameraMode,
          cameraRef.current.rideGate,
        );
        presentationConfiguredRef.current = true;
        setConfiguredRaceId(card.raceId);
      })
      .catch((caught: unknown) => {
        if (!cancelled && generationRef.current === generation) {
          failRef.current(
            caught instanceof Error ? caught.message : "Renderer failed",
          );
        }
      })
      .finally(() => window.clearTimeout(deadline));
    return () => {
      cancelled = true;
      window.clearTimeout(deadline);
    };
  }, [active, card.raceId, contextRevision, sceneReady]);

  useEffect(() => {
    const scene = sceneRef.current;
    const activePlayback = playbackRef.current;
    if (
      !active ||
      !scene ||
      configuredRaceId !== card.raceId ||
      !contextAvailableRef.current ||
      !presentationConfiguredRef.current
    )
      return;
    const generation = generationRef.current;
    let cancelled = false;
    let sequenceFallback = 0;
    let resultHandoff = 0;
    let frame = 0;
    const current = () =>
      !cancelled &&
      mountedRef.current &&
      activeRef.current &&
      contextAvailableRef.current &&
      generationRef.current === generation &&
      cardRef.current.raceId === card.raceId &&
      playbackKeyRef.current === playbackKey;
    const reveal = async () => {
      await scene.whenPresentationReady();
      if (!current()) return;
      if (activePlayback?.raceId === card.raceId) {
        callbacksRef.current.onPlaybackStarted?.(activePlayback.raceId);
      }
      // startRace installs the clock; the scene's RAF must sample and paint
      // that current clock before the parent may uncover the canvas.
      frame = window.requestAnimationFrame(() => {
        if (!current()) return;
        frame = window.requestAnimationFrame(() => {
          if (!current()) return;
          window.clearTimeout(deadline);
          setLifecycle("ready");
          callbacksRef.current.onLifecycle("ready");
          callbacksRef.current.onVisualReady(true);
        });
      });
    };
    const fail = (caught: unknown) => {
      if (current())
        failRef.current(
          caught instanceof Error ? caught.message : "Renderer failed",
        );
    };
    const deadline = window.setTimeout(
      () => fail(new Error("Race presentation timed out")),
      30_000,
    );
    const cleanup = () => {
      cancelled = true;
      window.clearTimeout(deadline);
      window.cancelAnimationFrame(frame);
      if (sequenceFallback) window.clearTimeout(sequenceFallback);
      if (resultHandoff) window.clearTimeout(resultHandoff);
    };
    if (
      !activePlayback ||
      !playbackKey ||
      activePlayback.raceId !== card.raceId
    ) {
      void reveal().catch(fail);
      return cleanup;
    }
    if (startedPlaybackRef.current !== playbackKey)
      callbacksRef.current.onVisualReady(false);
    void import("@renderer/race-physics.js")
      .then(async ({ planRace }) => {
        if (!current()) return;
        const plan = planRace(activePlayback.physics);
        if (sequenceRef.current.key !== playbackKey) {
          sequenceRef.current = {
            ...sequenceRef.current,
            key: playbackKey,
            state: "idle",
          };
        }
        // The casino ending: at the decided wire (third place home) the
        // scene replays the podium in slow motion, then hands the camera
        // to the winner circle for the official terminal. A wall-clock
        // fallback covers throttled RAF exactly like the donor host.
        const startSequence = () => {
          const sequence = sequenceRef.current;
          if (
            !current() ||
            !sequence.enabled ||
            !sequence.active ||
            sequence.key !== playbackKey ||
            sequence.state !== "idle"
          ) {
            return;
          }
          const scene = sceneRef.current;
          if (!scene) return;
          sequence.state = "replay";
          callbacksRef.current.onFinishSequence?.("replay");
          void scene
            .playFinishReplay()
            .catch(() => false)
            .then(() => {
              const current = sequenceRef.current;
              if (
                cancelled ||
                current.key !== playbackKey ||
                current.state !== "replay"
              ) {
                return;
              }
              sceneRef.current?.finishToResults();
              // The donor gives the winner-circle camera one short beat
              // before printing the terminal. Without it the stats panel
              // appears over the replay's final camera frame.
              resultHandoff = window.setTimeout(() => {
                const latest = sequenceRef.current;
                if (
                  cancelled ||
                  latest.key !== playbackKey ||
                  latest.state !== "replay"
                ) {
                  return;
                }
                latest.state = "done";
                callbacksRef.current.onFinishSequence?.("complete");
              }, RESULT_CAMERA_SETTLE_MS);
            });
        };
        // Nominal raceDurationMs is not a finish threshold. Wait for the
        // deterministic podium crossing just like the donor table does.
        const decidedS = plan.thirdFinish;
        const remainingMs =
          Math.max(0, (decidedS - getElapsedSeconds()) * 1_000) + 400;
        sequenceFallback = window.setTimeout(startSequence, remainingMs);
        scene.startRace(plan, getElapsedSeconds, (kind) => {
          if (kind === "decided" || kind === "raceOver") startSequence();
        });
        startedPlaybackRef.current = playbackKey;
        await reveal();
      })
      .catch(fail);
    return cleanup;
  }, [
    active,
    card.raceId,
    configuredRaceId,
    contextRevision,
    getElapsedSeconds,
    playbackKey,
  ]);

  useEffect(() => {
    sceneRef.current?.setCameraMode(cameraMode, rideGate);
  }, [cameraMode, rideGate]);

  return (
    <div
      aria-hidden={!active}
      className={`race-renderer race-renderer--3d${active ? "" : " is-hidden"}`}
      data-state={lifecycle}
    >
      <canvas ref={canvasRef} />
      {error ? (
        <div className="race-renderer__error">
          3D unavailable: {error}. Showing the verified fallback broadcast.
        </div>
      ) : null}
    </div>
  );
}

async function configureScene(
  scene: import("@renderer/scene.js").RaceScene,
  card: Racecard,
  assetsReady: () => Promise<void> = () => Promise.resolve(),
  isCurrent: () => boolean = () => true,
) {
  const environmentReady = scene.setEnvironment(
    environmentFor(card.stage, card.weather),
  );
  scene.setRaceDistance(card.distanceM, card.lapDistanceM);
  scene.setField({
    horses: card.runners.map((runner) => ({
      gate: runner.gate,
      name: runner.name,
      weight: runner.weight,
      silk: runner.silk,
      bloodline: runner.bloodline,
      appearance: runner.appearance,
    })),
  });
  await environmentReady;
  await assetsReady();
  if (isCurrent()) await scene.whenPresentationReady();
}

function environmentFor(stage: string, weather: string) {
  const legacy: Record<string, string> = {
    "canyon:clear": "canyon-day",
    "canyon:rain": "canyon-storm",
    "rose:mist": "rose-dusk",
    "desert:clear": "dune-gold",
    "desert:dust": "dune-storm",
    "midnight:clear": "midnight",
  };
  return legacy[`${stage}:${weather}`] ?? { stage, weather };
}
